import { useTranslation } from 'react-i18next';
import {
  getLocaleCurrencyOrPlaceholder,
  getLocaleNumberOrPlaceholder,
  getValueOrPlaceholder,
  parseDateString,
} from '@/utils/helper';
import { useProductDetailsTable } from '@/hooks/useProductDetailsTable';

import styles from '../../ProductView/ProductMetadata/ProductMetadataTable.module.scss';
import { formatGenre } from '@/components/common/ProductUtilities';
import {
  LabelEntry,
  ParentLabelEntry,
  PreOrderLabel,
  ProductEntry,
  ProductDetailsTableProps,
  ProjectEntry,
  RequiredMessage,
  YesNo,
} from '../../ProductView/ProductMetadata/ProductMetadataCommon';
import { usePriceTab } from '@/hooks/usePriceTab';
import { AlbumState } from '@/data/models/albumState';
import { translateMerchandiseProductType } from '@/helpers/translateProductType';

export default function MerchProductMetadataTable({
  errors,
  errorFlag,
  album,
  project,
  onToggleLabelInfoModal,
  onToggleParentLabelInfoModal,
  changeRequest,
}: ProductDetailsTableProps) {
  const { t } = useTranslation();
  const {
    canUserViewInternalFields,
    canUserReadProjectService,
    canUserAccessProjectServiceUi,
    hasRequiredError,
    albumCountryOfManufactureDisplayName,
  } = useProductDetailsTable({ errors, errorFlag, album });

  const { allRows } = usePriceTab(album, changeRequest);
  const primaryPriceRow = allRows.find(
    (row) => row.type === t('product.pricing.salesTerritoritoryTeam')
  );
  const primaryPriceDisplay =
    primaryPriceRow &&
    primaryPriceRow.name &&
    primaryPriceRow.price !== '' &&
    primaryPriceRow.price !== '--'
      ? `${primaryPriceRow.name} (${primaryPriceRow.currency}) ${primaryPriceRow.price}`
      : null;

  return (
    <div
      className={
        styles['product-details-grid'] +
        ' ' +
        ((album.albumMetaData?.versions.length ?? 0) > 1
          ? styles['product-details-multiple-versions']
          : '')
      }
    >
      {/* Start of grouping 1 */}

      <ProductEntry
        name={t('product.editProduct.name')}
        value={album.title}
        hasRequiredError={hasRequiredError('Title')}
        column={[1, 1]}
      />

      <ProductEntry
        name={t('product.editProduct.version')}
        value={getValueOrPlaceholder(album.albumMetaData?.versions[0])}
        column={[1, 1]}
      />

      <ProductEntry
        name={t('product.editProduct.subType')}
        value={getValueOrPlaceholder(
          translateMerchandiseProductType(
            album.merchandiseMetadata?.merchandiseType?.name
          )
        )}
        hasRequiredError={hasRequiredError('MerchandiseType')}
        column={[1, 1]}
      />

      <ProductEntry
        name={t('product.editProduct.color')}
        value={getValueOrPlaceholder(album.merchandiseMetadata?.color)}
        hasRequiredError={hasRequiredError('Color')}
        column={[1, 1]}
      />

      <ProductEntry
        name={t('product.editProduct.size')}
        value={getValueOrPlaceholder(album.merchandiseMetadata?.size)}
        hasRequiredError={hasRequiredError('Size')}
        column={[1, 1]}
      />

      <LabelEntry
        album={album}
        canUserViewInternalFields={canUserViewInternalFields}
        onToggleLabelInfoModal={onToggleLabelInfoModal}
        hasRequiredError={hasRequiredError}
      />

      <ParentLabelEntry
        album={album}
        canUserViewInternalFields={canUserViewInternalFields}
        onToggleParentLabelInfoModal={onToggleParentLabelInfoModal}
        hasRequiredError={hasRequiredError}
      />

      <ProductEntry
        name={t('product.editProduct.GTIN')}
        value={getValueOrPlaceholder(album.gtin)}
        hasRequiredError={hasRequiredError('GTIN')}
        column={[1, 1]}
        redirectUrl={
          album.albumState === AlbumState.Released
            ? `/Merchandise/ProductDetails/${album.id}`
            : null
        }
      />

      <ProductEntry
        name={t('product.editProduct.releaseDate')}
        value={getValueOrPlaceholder(parseDateString(album.releaseDate))}
        hasRequiredError={hasRequiredError('ReleaseDate')}
        column={[1, 2]}
      />

      {/* End of grouping 1 */}

      {/* Start of grouping 2 */}

      <ProductEntry
        name={t('product.editProduct.preOrderDate')}
        value={
          <PreOrderLabel
            enabled={album.albumMetaData?.retailPreOrder}
            date={album.albumMetaData?.retailPreOrderDate}
            time={album.albumMetaData?.retailPreOrderTime}
          />
        }
        hasRequiredError={hasRequiredError('RetailPreOrderDate')}
        column={[1, 2]}
      />

      <ProductEntry
        name={t('product.pricing.salesTerritoritoryTeam')}
        value={getValueOrPlaceholder(primaryPriceDisplay)}
        column={[1, 2]}
      />

      <ProductEntry
        name={t('product.editProduct.accountExclusiveRelease')}
        value={
          <>
            <YesNo value={album.albumMetaData?.exclusiveReleaseAccount} />{' '}
            {album.albumMetaData?.exclusiveReleaseAccount &&
              (hasRequiredError('ExclusiveReleaseAccountName') ? (
                <RequiredMessage withParenthesis />
              ) : (
                `(${album.albumMetaData?.exclusiveReleaseAccountName})`
              ))}
          </>
        }
        column={[2, 2]}
      />

      <ProductEntry
        name={t('product.editProduct.catalog#')}
        value={getValueOrPlaceholder(album.catalogNumber)}
        hasRequiredError={hasRequiredError('CatalogNumber')}
        column={[2, 2]}
      />

      <ProductEntry
        name={t('product.editProduct.genre')}
        value={getValueOrPlaceholder(formatGenre(album.albumMetaData?.genre))}
        hasRequiredError={hasRequiredError('Genre')}
        column={[2, 2]}
      />

      <ProductEntry
        name={t('product.editProduct.ingroovesManufacturingProduct')}
        value={
          <YesNo
            value={
              album.albumMetaData?.manufacturing?.isManufacturedByIngrooves
            }
          />
        }
        column={[2, 2]}
      />

      <ProductEntry
        name={t('product.editProduct.countryOfManufacture')}
        value={getValueOrPlaceholder(albumCountryOfManufactureDisplayName)}
        hasRequiredError={hasRequiredError('CountryOfManufacture')}
        column={[2, 2]}
      />

      {/* End of grouping 2  */}

      {/* Start of grouping 3 */}

      <ProductEntry
        name={t('product.editProduct.manufacturingCostPerUnit')}
        value={getLocaleCurrencyOrPlaceholder(
          album.albumMetaData?.manufacturing?.manufacturingCostPerUnit,
          album.albumMetaData?.manufacturing?.currency
        )}
        column={[2, 3]}
      />

      <ProductEntry
        name={t('product.editProduct.projectedPhysicalIO')}
        value={getLocaleNumberOrPlaceholder(
          album.albumMetaData?.salesProjection?.projectedPhysicalIO
        )}
        hasRequiredError={hasRequiredError('ProjectedPhysicalIO')}
        column={[2, 3]}
      />

      <ProjectEntry
        project={project}
        canUserReadProjectService={canUserReadProjectService}
        canUserAccessProjectServiceUi={canUserAccessProjectServiceUi}
      />

      {/* End of grouping 3 */}
    </div>
  );
}
